import { LessonProgressStatus, resolveVideoCompletionPercent, type TContentBlockBase } from '@onesource/schemas';
import { useCompleteBlock, useGetSpace, useUpdateProgress } from '@onesource/services';
import { useParams } from '@tanstack/react-router';
import Player from '@vimeo/player';
import { type RefObject, useCallback, useEffect, useMemo, useRef } from 'react';

import { useContentRouteOptional } from './use-content-route';

/**
 * Watched-percent buckets reported to tracking as VIDEO_PROGRESS_25/50/75/100.
 * 100 is reported from the player's `ended` event (timeupdate rarely lands on
 * exactly 100) and, unlike VIDEO_COMPLETE, is independent of the completion
 * threshold — so "watched to the end" stays visible when a module completes
 * videos at 25/50/75 %.
 */
export const VIDEO_MILESTONES = [25, 50, 75, 100] as const;

/**
 * Effective "video completed" threshold for a block: the block's own override,
 * else the module (content sub-space) setting, else 100. A single-lesson
 * program is its own module, so it falls back to the program space — the same
 * rule `useResolvedBlock` applies. The module space is already in the React
 * Query cache by the time a player mounts (every lesson / item route fetches
 * it), so this adds no request even with many players on one page.
 */
export const useVideoCompletionPercent = (block: Pick<TContentBlockBase, 'metadata'>): number => {
  const contentRoute = useContentRouteOptional();
  const { id: paramSpaceId } = useParams({ strict: false });
  const moduleId = contentRoute?.subSpaceId ?? contentRoute?.programId ?? paramSpaceId ?? '';
  const { data: moduleSpace } = useGetSpace(moduleId);
  return useMemo(
    () => resolveVideoCompletionPercent(block.metadata, moduleSpace?.settings),
    [block.metadata, moduleSpace?.settings],
  );
};

export interface UseVideoProgressTrackingOptions {
  block: Pick<TContentBlockBase, 'id' | 'vimeoId'>;
  lessonId: string;
  /** 1–100 (see `useVideoCompletionPercent`). 100 = complete on `ended` only. */
  completionPercent: number;
  /** The `<iframe src="https://player.vimeo.com/…">` the Vimeo SDK attaches to. */
  iframeRef: RefObject<HTMLIFrameElement | null>;
  /** Element whose first 25 %-visibility records the VIDEO_VIEW ping. */
  containerRef: RefObject<HTMLElement | null>;
}

export interface UseVideoProgressTrackingResult {
  /** Mark-as-Watched: completes the block regardless of the threshold, like today. */
  markAsWatched: () => void;
}

/**
 * The one place lesson videos report progress. Every template (inline hero,
 * inline compact, playlist hero, item page, single-lesson program, event
 * replay) renders the same `VideoBlockPlayer`, which delegates here — so the
 * pings and the completion rule cannot drift between layouts.
 *
 * Per player mount it sends, each at most once:
 *  - VIDEO_VIEW when the player first scrolls 25 % into view
 *  - VIDEO_PLAY on the first `play` (the API maps a plain ping to the block type's view/play action)
 *  - VIDEO_PROGRESS_25 / 50 / 75 as the watched percent crosses each bucket
 *    (a seek straight to 80 % fires all three) and VIDEO_PROGRESS_100 on `ended`
 *    — sent as `percentWatched: <bucket>` with no `logAction`, which is what
 *    `logBlockView` needs to log a milestone
 *  - completion (`POST /blocks/:id/complete`) when `percent >= completionPercent`,
 *    or on `ended` — which is the only path when the threshold is 100
 *
 * Vimeo listeners are registered exactly once per block: handlers read the
 * latest mutations / threshold through refs instead of being re-created, so a
 * module setting that resolves after mount is honoured by the next `timeupdate`.
 */
export function useVideoProgressTracking({
  block,
  lessonId,
  completionPercent,
  iframeRef,
  containerRef,
}: UseVideoProgressTrackingOptions): UseVideoProgressTrackingResult {
  const { mutate: updateProgress } = useUpdateProgress(lessonId);
  const { mutate: completeBlock } = useCompleteBlock();

  // Latest values for the once-registered listeners.
  const updateProgressRef = useRef(updateProgress);
  const completeBlockRef = useRef(completeBlock);
  const thresholdRef = useRef(completionPercent);
  const blockIdRef = useRef(block.id);
  updateProgressRef.current = updateProgress;
  completeBlockRef.current = completeBlock;
  thresholdRef.current = completionPercent;
  blockIdRef.current = block.id;

  // Once-per-mount guards. The playlist hero keeps one player mounted while
  // swapping the block underneath it, so they reset whenever the block changes
  // (this effect is declared first, so it runs before the observers below).
  const viewTracked = useRef(false);
  const playTracked = useRef(false);
  const firedMilestones = useRef(new Set<number>());
  const completed = useRef(false);
  const lastIntPercent = useRef(-1);
  useEffect(() => {
    viewTracked.current = false;
    playTracked.current = false;
    firedMilestones.current = new Set<number>();
    completed.current = false;
    lastIntPercent.current = -1;
  }, [block.id]);

  // View: once, when the block first scrolls into view (inline pages hold many
  // players, so never all at once on load).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting && !viewTracked.current) {
          viewTracked.current = true;
          updateProgressRef.current({
            status: LessonProgressStatus.IN_PROGRESS,
            lastBlockId: blockIdRef.current,
            logAction: 'VIDEO_VIEW',
          });
          observer.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [block.id, containerRef]);

  // Player: play / timeupdate / ended, registered once per video.
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !block.vimeoId) {
      return;
    }
    const player = new Player(iframe);

    const complete = () => {
      if (completed.current) {
        return;
      }
      completed.current = true;
      completeBlockRef.current(blockIdRef.current);
    };

    const onPlay = () => {
      if (playTracked.current) {
        return;
      }
      playTracked.current = true;
      updateProgressRef.current({ status: LessonProgressStatus.IN_PROGRESS, lastBlockId: blockIdRef.current });
    };

    const reportMilestones = (percent: number) => {
      for (const milestone of VIDEO_MILESTONES) {
        if (percent >= milestone && !firedMilestones.current.has(milestone)) {
          firedMilestones.current.add(milestone);
          updateProgressRef.current({
            status: LessonProgressStatus.IN_PROGRESS,
            lastBlockId: blockIdRef.current,
            percentWatched: milestone,
          });
        }
      }
    };

    const onTimeUpdate = (event: Player.TimeEvent) => {
      const percent = event.duration > 0 ? (event.seconds / event.duration) * 100 : event.percent * 100;
      // Vimeo fires ~4×/s; do the work at most once per whole percent.
      const intPercent = Math.floor(percent);
      if (intPercent === lastIntPercent.current) {
        return;
      }
      lastIntPercent.current = intPercent;

      reportMilestones(percent);

      const threshold = thresholdRef.current;
      if (threshold < 100 && percent >= threshold) {
        complete();
      }
    };

    // `ended` is the reliable 100 % signal; it also back-fills any bucket a
    // seek skipped over, then completes (a no-op if the threshold already did).
    const onEnded = () => {
      reportMilestones(100);
      complete();
    };

    player.on('play', onPlay);
    player.on('timeupdate', onTimeUpdate);
    player.on('ended', onEnded);
    return () => {
      // Never `destroy()` — that removes the iframe React owns.
      player.off('play', onPlay);
      player.off('timeupdate', onTimeUpdate);
      player.off('ended', onEnded);
    };
  }, [block.id, block.vimeoId, iframeRef]);

  const markAsWatched = useCallback(() => completeBlock(block.id), [block.id, completeBlock]);

  return { markAsWatched };
}
